import React, {ComponentPropsWithRef, memo, useCallback, useEffect, useRef, useState} from 'react'
import {EmblaCarouselType, EmblaEventType, EmblaOptionsType} from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import {MyCard, MyText} from "../../../common";
import {Col, Row, Grid} from "antd";
import dayjs from "dayjs";
import {Link} from "react-router-dom";
import {StyledCarousel} from "./styles";
import ClassNames from "embla-carousel-class-names";
import SectionComma from '../../../../assets/images/pages/Main/section8-comma.webp'

type PropType = {
  slides: any
  options?: EmblaOptionsType
}

type UseDotButtonType = {
  selectedIndex: number
  scrollSnaps: number[]
  onDotButtonClick: (index: number) => void
}


const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max)

const TWEEN_FACTOR_BASE = 0.84



const Component = memo(({item}: any) => {
  const {useBreakpoint} = Grid;
  const screens = useBreakpoint();

  const isMobile = screens.xs;
  const isTablet = screens.md && !screens.xl && !screens.xxl;

  return (
   <MyCard
    style={{
      width: screens.xs ? '350px' : undefined,
      height: isTablet ? 'auto' : screens.xs ? 'auto' : '320px',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Row gutter={[24, 16]} style={{ flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <img
        src={SectionComma}
        style={{
          position: 'absolute',
          top: '5%',
          left: screens.xs ? '7%' : '5%',
          width: screens.xs ? '32px' : '40px',
          height: screens.xs ? '24px' :'30px',
        }}
      />
      <Col span={24} style={{ marginTop: screens.xs ? '28px' : '32px' }}>
        <MyText color='#0D1216' size={screens.xs ? 16 : 18} weight={400} lineHeight={screens.xs ? '18px' : '28px'}>
          {item.comment}
        </MyText>
      </Col>
      <Col span={24}>
        <MyText color='#0A033C' size={screens.xs ? 16 : 18} weight={700} style={{ fontFamily: 'Nunito' }}>
          {item.author}
        </MyText>
      </Col>
    </Row>
  </MyCard>
  )
})

const EmblaCarousel: React.FC<PropType> = (props) => {
  const { slides, options } = props
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [ClassNames()])
  const tweenFactor = useRef(0)
  const {useBreakpoint} = Grid;
  const screens = useBreakpoint();

  const isMobile = screens.xs;
  const isTablet = screens.md && !screens.xl && !screens.xxl;
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi)

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length
  }, [])

  const tweenOpacity = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine()
      const scrollProgress = emblaApi.scrollProgress()
      const slidesInView = emblaApi.slidesInView()
      const isScrollEvent = eventName === 'scroll'

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress
        const slidesInSnap = engine.slideRegistry[snapIndex]

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target()

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target)

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress)
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress)
                }
              }
            })
          }

          const tweenValue = 2 - Math.abs(diffToTarget * tweenFactor.current)
          const opacity = numberWithinRange(tweenValue, 0, 1).toString()
          emblaApi.slideNodes()[slideIndex].style.opacity = opacity
        })
      })
    },
    []
  )

  useEffect(() => {
    if (!emblaApi) return

    setTweenFactor(emblaApi)
    tweenOpacity(emblaApi)
    emblaApi
      .on('reInit', setTweenFactor)
      .on('reInit', tweenOpacity)
      .on('scroll', tweenOpacity)
      .on('slideFocus', tweenOpacity)
  }, [emblaApi, tweenOpacity])


  return (
    <StyledCarousel isTablet={isTablet}
    //  style={isMobile ? { height: '100%' } : {}}
     >
      <section className="embla" style={isMobile ? { height: '100%' } : {}}>
        <div className="embla__viewport" ref={emblaRef} style={isMobile ? { height: '100%' } : {height: 'auto'}}>
          <div className="embla__container">
            {slides.map((item: any, index: number) => (
              <div className="embla__slide embla__class-names" key={index}>
                <Component item={item}/>
              </div>
            ))}
          </div>
        </div>

        <div className="embla__controls">
          <div className="embla__dots">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={'embla__dot'.concat(
                  index === selectedIndex ? ' embla__dot--selected' : ''
                )}
              />
            ))}
          </div>
        </div>
      </section>
    </StyledCarousel>

  )
}


export const useDotButton = (
  emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick
  }
}

type PropTypeDot = ComponentPropsWithRef<'button'>

const DotButton: React.FC<PropTypeDot> = (props) => {
  const { children, ...restProps } = props

  return (
    <button type="button" {...restProps}>
      {children}
    </button>
  )
}


export default EmblaCarousel